import * as XLSX from "xlsx";

// Turns an attendance report into the rows shown on screen and written to file.
//
// The table and the download are built from the same row objects on purpose:
// what an admin reviews is exactly what lands in the spreadsheet, column for
// column. Durations are decimal hours, which is what payroll multiplies by --
// "7h 32m" is readable but useless in a formula.

/** Decimal hours to 2dp. */
export const hours = (ms) => Math.round(((ms || 0) / 3600000) * 100) / 100;
export const minutes = (ms) => Math.round((ms || 0) / 60000);
const localTime = (v) => (v ? new Date(v).toLocaleString() : "");

export const SHEETS = {
  summary: { key: "summary", label: "Summary", hint: "One row per agent for the range" },
  daily: { key: "daily", label: "Daily Register", hint: "One row per agent per day" },
  detail: { key: "detail", label: "Shift Detail", hint: "Every clock-in, break and clock-out" },
};

export const summaryRows = (report) =>
  (report?.summary || []).map((s) => ({
    Agent: s.agentName || s.agentUserId,
    "User ID": s.agentUserId,
    "Days Present": s.daysPresent,
    Shifts: s.shifts,
    "Productive (h)": hours(s.productiveMs),
    "Break (h)": hours(s.breakMs),
    "Total Logged (h)": hours(s.workedMs),
    "Avg Productive / Day (h)": hours(s.avgProductiveMsPerDay),
    Breaks: s.breakCount,
    "Late Days": s.lateCount,
    "Total Late (min)": minutes(s.lateMs),
    "Auto Clock-Outs": s.autoClockedOutCount,
  }));

export const dailyRows = (report) =>
  (report?.daily || []).map((d) => ({
    Date: d.date,
    Agent: d.agentName || d.agentUserId,
    "User ID": d.agentUserId,
    "First In": localTime(d.firstInAt),
    "Last Out": localTime(d.lastOutAt),
    Shifts: d.shifts,
    "Productive (h)": hours(d.productiveMs),
    "Break (h)": hours(d.breakMs),
    "Total (h)": hours(d.workedMs),
    "Late (min)": minutes(d.lateMs),
  }));

export const detailRows = (report) =>
  (report?.detail || []).map((r) => ({
    Date: r.date,
    Agent: r.agentName,
    "User ID": r.agentUserId,
    "Clock In": localTime(r.clockInAt),
    "Clock Out": localTime(r.clockOutAt),
    Status: r.status,
    "Productive (h)": hours(r.totals.productiveMs),
    "Break (h)": hours(r.totals.breakMs),
    "Total (h)": hours(r.totals.workedMs),
    "Late (min)": minutes(r.lateBy),
    Breaks: r.breaks.length,
    "Break Detail": r.breaks
      .map((b) => `${b.typeLabel || b.type}: ${minutes(b.durationMs)}m${b.overran ? " (over)" : ""}`)
      .join(" | "),
    "Auto Clock-Out": r.autoClockedOut ? "Yes" : "",
    Note: r.clockOutNote || "",
    "Clock In Location": r.clockInLocation ? `${r.clockInLocation.lat}, ${r.clockInLocation.lng}` : "",
    "Clock Out Location": r.clockOutLocation ? `${r.clockOutLocation.lat}, ${r.clockOutLocation.lng}` : "",
    "Clock In Device": r.clockInDevice?.label || "",
    "Clock Out Device": r.clockOutDevice?.label || "",
  }));

export const rowsFor = (report, sheet) =>
  sheet === "summary" ? summaryRows(report) : sheet === "daily" ? dailyRows(report) : detailRows(report);

const autoWidth = (rows) =>
  !rows.length
    ? []
    : Object.keys(rows[0]).map((key) => ({
        wch: Math.min(42, Math.max(key.length + 2, ...rows.map((r) => String(r[key] ?? "").length + 2))),
      }));

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const toCsv = (rows) => {
  if (!rows.length) return "";
  const head = Object.keys(rows[0]);
  const body = rows.map((r) => head.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","));
  return [head.map((h) => `"${h}"`).join(","), ...body].join("\n");
};

export const downloadCsv = (report, sheet) => {
  const rows = rowsFor(report, sheet);
  if (!rows.length) return false;
  const stamp = `${report.range.from}_to_${report.range.to}`;
  download(new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8;" }), `attendance_${sheet}_${stamp}.csv`);
  return true;
};

/** Multi-sheet workbook, led by a cover sheet saying what it covers. */
export const downloadWorkbook = (report, agentLabel = "All agents") => {
  const wb = XLSX.utils.book_new();
  const t = report.totals || {};

  // A workbook found on someone's desktop in March should still explain what
  // it covers and when it was produced.
  const cover = [
    { Field: "Report", Value: "Attendance Report" },
    { Field: "Range", Value: `${report.range.from} to ${report.range.to} (${report.range.days} days)` },
    { Field: "Agent", Value: agentLabel },
    { Field: "Generated", Value: new Date(report.generatedAt).toLocaleString() },
    { Field: "Timezone", Value: report.timezone },
    { Field: "Agents with activity", Value: t.agents || 0 },
    { Field: "Shifts", Value: t.shifts || 0 },
    { Field: "Productive (h)", Value: hours(t.productiveMs) },
    { Field: "Break (h)", Value: hours(t.breakMs) },
    { Field: "Total logged (h)", Value: hours(t.workedMs) },
    { Field: "Late arrivals", Value: t.lateCount || 0 },
  ];
  const coverSheet = XLSX.utils.json_to_sheet(cover);
  coverSheet["!cols"] = [{ wch: 24 }, { wch: 46 }];
  XLSX.utils.book_append_sheet(wb, coverSheet, "Report Info");

  const add = (name, rows) => {
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = autoWidth(rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  add("Summary", summaryRows(report));
  add("Daily Register", dailyRows(report));
  add("Shift Detail", detailRows(report));

  if (wb.SheetNames.length === 1) return false;
  XLSX.writeFile(wb, `attendance_report_${report.range.from}_to_${report.range.to}.xlsx`);
  return true;
};
